import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

// Socket.io client'ı dinamik import ile yükle
let io = null;

const initializeSocket = async () => {
    if (!io) {
        try {
            const socketModule = await import('socket.io-client');
            io = socketModule.default || socketModule.io;
        } catch (error) {
            console.warn('Socket.io-client yüklenemedi:', error);
            return null;
        }
    }
    return io;
};

export const useSocket = (options = {}) => {
    const { token, user } = useAuth();
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const socketRef = useRef(null);

    // Prod ortamda websocket upgrade yapılamıyorsa polling-only zorlamak için ENV destekleyelim
    // .env veya package.json script üzerinden REACT_APP_SOCKET_POLLING_ONLY=true geçilebilir
    const WEBSOCKET_ENABLED = !(process.env.REACT_APP_SOCKET_POLLING_ONLY === 'true');

    useEffect(() => {
        let mounted = true;

        if (!WEBSOCKET_ENABLED) {
            console.log('🔌 WebSocket devre dışı - polling modu aktif');
            if (mounted) {
                setConnected(false);
                setError('WebSocket devre dışı - polling modu kullanılıyor');
            }
            return;
        }

        const connectSocket = async () => {
            try {
                const socketIO = await initializeSocket();
                
                if (!socketIO || !token || !user) {
                    return;
                }

                // Socket bağlantısı oluştur
                // Polling-only modunu isteğe bağlı zorla
                const pollingOnly = process.env.REACT_APP_SOCKET_POLLING_ONLY === 'true';
                const newSocket = socketIO(
                    process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3000',
                    {
                        transports: pollingOnly ? ['polling'] : ['polling', 'websocket'],
                        upgrade: pollingOnly ? false : true,
                        timeout: 20000,
                        forceNew: true,
                        withCredentials: true,
                        autoConnect: true,
                        reconnection: true,
                        reconnectionDelay: 1000,
                        reconnectionAttempts: 5
                    }
                );

                socketRef.current = newSocket;

                // Bağlantı event'leri
                newSocket.on('connect', () => {
                    console.log('🔌 Socket.io bağlantısı kuruldu:', newSocket.id);
                    console.log('🔌 Transport:', newSocket.io.engine.transport.name);
                    if (process.env.REACT_APP_SOCKET_POLLING_ONLY === 'true') {
                        console.log('ℹ️ Polling-only modu aktif (REACT_APP_SOCKET_POLLING_ONLY=true)');
                    }
                    
                    // Kimlik doğrulama
                    const isAdmin = user?.app_metadata?.roles?.includes('admin') || user?.role === 'admin';
                    
                    // GEÇİCİ: Test için admin bypass (daha sonra kaldırılacak)
                    const isTestUser = user?.email === 'hikibo06@gmail.com';
                    const finalAdminCheck = isAdmin || isTestUser;
                    
                    console.log('🔐 Admin kontrolü:', { 
                        userRole: user?.role, 
                        appMetadata: user?.app_metadata, 
                        isAdmin,
                        isTestUser,
                        finalAdminCheck
                    });
                    
                    newSocket.emit('authenticate', {
                        token: token,
                        userId: user.id,
                        isAdmin: finalAdminCheck
                    });
                    
                    if (mounted) {
                        setConnected(true);
                        setError(null);
                    }
                });

                newSocket.on('authenticated', (data) => {
                    console.log('✅ Socket kimlik doğrulandı:', data);
                    
                    // Admin ise tracking room'a katıl
                    if (user.role === 'admin') {
                        newSocket.emit('join-tracking-room');
                    }
                });

                newSocket.on('auth-error', (data) => {
                    console.error('❌ Socket kimlik doğrulama hatası:', data);
                    if (mounted) {
                        setError(data.message);
                    }
                });

                newSocket.on('disconnect', (reason) => {
                    console.log('🔌 Socket bağlantısı koptu:', reason);
                    if (mounted) {
                        setConnected(false);
                    }
                });

                newSocket.on('connect_error', (error) => {
                    console.error('🔌 Socket bağlantı hatası:', error);
                    if (mounted) {
                        setError(error.message);
                        setConnected(false);
                    }
                });

                // Ping-pong for connection health
                newSocket.on('pong', (data) => {
                    console.log('🏓 Socket pong alındı:', data.timestamp);
                });

                if (mounted) {
                    setSocket(newSocket);
                }

            } catch (error) {
                console.error('Socket başlatma hatası:', error);
                if (mounted) {
                    setError(error.message);
                }
            }
        };

        if (token && user) {
            connectSocket();
        }

        return () => {
            mounted = false;
            if (socketRef.current) {
                console.log('🔌 Socket bağlantısı kapatılıyor...');
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [token, user]);

    // Socket event listener hook'u
    const useSocketEvent = (event, handler) => {
        useEffect(() => {
            if (socket && connected) {
                socket.on(event, handler);
                return () => socket.off(event, handler);
            }
        }, [socket, connected, event, handler]);
    };

    // Socket emit helper
    const emit = (event, data) => {
        if (socket && connected) {
            socket.emit(event, data);
            return true;
        }
        console.warn('Socket bağlı değil, emit edilemedi:', event);
        return false;
    };

    // Ping gönder
    const ping = () => {
        if (socket && connected) {
            socket.emit('ping');
        }
    };

    return {
        socket,
        connected,
        error,
        emit,
        ping,
        useSocketEvent
    };
};

export default useSocket;
